//define get(), post() any standard error handling logic, api headers etc in here
import { CustomError } from "../utils/CustomError";
import { msalInstance } from "../index";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import { appInsights } from "../appInsights";

const getApiHeaders = async () => {
	const headers = new Headers();

	if (process.env.REACT_APP_ENVIRONMENT === "AZURE") {
		const bearer = await getMsalToken();
		headers.append("Authorization", "Bearer " + bearer);
	}
	headers.append("Content-Type", "application/json");
	headers.append("Access-Control-Request-Headers", "*");
	headers.append("Access-Control-Request-Method", "*");
	headers.append("pragma", "no-cache");
	headers.append("cache-control", "no-cache");

	return headers;
};

const getApiHeadersLookup = async () => {
	const headers = new Headers();
	headers.append("Content-Type", "application/json");
	headers.append("Access-Control-Request-Headers", "*");
	headers.append("Access-Control-Request-Method", "*");
	headers.append("cache-control", "max-age=3600");

	return headers;
};

export const getMsalToken = async () => {
	let token = "";

	if (process.env.REACT_APP_ENVIRONMENT === "AZURE") {
		//instance and account can pass from compoent then easier
		//active account is pinned in index.jsx - getAllAccounts()[0] is only a
		//fallback, its order is undefined when more than one account is cached
		const account =
			msalInstance.getActiveAccount() ??
			msalInstance.getAllAccounts()[0];

		const request = {
			scopes: [process.env.REACT_APP_SCOPES], //["User.Read"],
			account,
		};

		// Silently acquires an access token which is then attached to a request for web api
		await msalInstance
			.acquireTokenSilent(request)
			.then((response) => {
				token = response.accessToken;
			})
			.catch((e) => {
				msalInstance.acquireTokenRedirect(request).then((response) => {
					token = response.accessToken;
				});
			});
	}
	return token;
};

const fetchWithErrorHanlding = async (apiUrl, options, timeout) => {
	try {
		timeout = timeout * 1000; //change to miliseconds

		const controller = new AbortController();
		const id = setTimeout(() => {
			controller.abort();
		}, timeout);

		const response = await fetch(apiUrl, {
			...options,
			signal: controller.signal,
		});

		clearTimeout(id);

		const { ok, status } = response;
		if (!ok) {
			const errorMessage = await response.json();
			throw new CustomError(
				errorMessage.error,
				response.status,
				errorMessage.message
			);
		} else {
			if (status !== 204) return await response.json();
			else return null;
		}
	} catch (error) {
		if (error.name === "AbortError") {
			throw new CustomError("AbortError");
		} else if (error instanceof CustomError) throw error;
		else throw new CustomError(error.message);
	}
};

export const get = async (
	apiUrl,
	isAuth = true,
	customHeaders = null,
	timeout = 20
) => {
	try {
		var headers = isAuth
			? await getApiHeaders()
			: await getApiHeadersLookup();

		customHeaders &&
			customHeaders.forEach((h) => {
				headers.append(h.key, h.value);
			});

		const options = {
			method: "GET",
			credentials: "include",
			headers: headers,
		};

		if (
			process.env.REACT_APP_INSTRUMENTATION_KEY !== undefined &&
			process.env.REACT_APP_INSTRUMENTATION_KEY &&
			process.env.REACT_APP_INSTRUMENTATION_KEY !== ""
		) {
			appInsights.trackTrace({
				message: apiUrl,
				severityLevel: SeverityLevel.Information,
				properties: { apiUrl },
			});
		}
		return await fetchWithErrorHanlding(apiUrl, options, timeout);
	} catch (error) {
		if (
			process.env.REACT_APP_INSTRUMENTATION_KEY !== undefined &&
			process.env.REACT_APP_INSTRUMENTATION_KEY &&
			process.env.REACT_APP_INSTRUMENTATION_KEY !== ""
		) {
			appInsights.trackException({
				message: "Get Exception",
				error: error,
				exception: error,
				severityLevel: SeverityLevel.Error,
				properties: { ...error },
			});
		}
		if (error instanceof CustomError) throw error;
		else throw new CustomError(error.message);
	}
};

export const post = async (apiUrl, data, timeout = 20) => {
	try {
		const options = {
			method: "POST",
			credentials: "include",
			headers: await getApiHeaders(),
			body: JSON.stringify(data),
		};
		if (
			process.env.REACT_APP_INSTRUMENTATION_KEY !== undefined &&
			process.env.REACT_APP_INSTRUMENTATION_KEY &&
			process.env.REACT_APP_INSTRUMENTATION_KEY !== ""
		) {
			appInsights.trackTrace({
				message: apiUrl,
				severityLevel: SeverityLevel.Information,
				properties: { data },
			});
		}
		return await fetchWithErrorHanlding(apiUrl, options, timeout);
	} catch (error) {
		if (
			process.env.REACT_APP_INSTRUMENTATION_KEY !== undefined &&
			process.env.REACT_APP_INSTRUMENTATION_KEY &&
			process.env.REACT_APP_INSTRUMENTATION_KEY !== ""
		) {
			appInsights.trackException({
				message: "Post Exception",
				error: error,
				exception: error,
				severityLevel: SeverityLevel.Error,
				properties: { error, data },
			});
		}
		if (error instanceof CustomError) throw error;
		else throw new CustomError(error.message);
	}
};
